import { html, LitElement, nothing, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { PlanConfig, PlanId, PurchaseCardConfig } from '../types';
import { translateFn } from '../../../../../../utils/translator.helper';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { InputWidthValues } from '../../../../../../ui/gzInput';
import { classMap } from 'lit/directives/class-map.js';
import moment from 'moment-timezone';
import { MAX_SEATS_EXCEEDED_PRODUCT_PAGES } from '../../constants';
import { SpinnerWidth } from '../../../../../../ui/gzProgressSpinner/types';

@customElement('gz-purchase-card-layout')
export class GzPurchaseCardLayoutWebComponent extends LitElement {
  static get styles() {
    return styles;
  }

  @property({ type: Object, attribute: true, reflect: true })
  config: PurchaseCardConfig;

  render(): TemplateResult {
    const config = this.config;

    return html`
      <div class="container">
        <gz-text
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="text-uppercase"
          automationId="ipb-purchase-card-title"
        >
          ${config.title}
        </gz-text>

        ${this.renderPlan(config.currentPlan, PlanId.CurrentPlan)}
        <!--        -->
        ${this.renderAutoRenewal()}
        <!--        -->

        ${config.maxSeatsAlreadyExceeded
          ? this.renderMaxSeatsAlreadyExceededText()
          : config.showBuyNewLicense
          ? this.renderBuyNewLicense()
          : html`
              ${this.renderPlan(config.newPlan, PlanId.NewPlan)}
              <!--        -->
              ${this.renderPrice()}
              <!--        -->
              ${this.renderBuyButton()}
            `}
      </div>
    `;
  }

  private renderPlan(plan: PlanConfig, planId: PlanId): TemplateResult | undefined {
    const isNewPlan = planId === PlanId.NewPlan;
    const years = (isNewPlan ? '+' : '') + plan.years;
    const automationIdPrefix = `ipb-purchase-card-${isNewPlan ? 'new-license' : 'current-license'}`;

    return html`
      <div
        class="content pt-3 ${classMap({
          'full-width': plan.years || false,
        })}"
      >
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.MEDIUM}"
          class="text-uppercase"
          automationId="${automationIdPrefix}-title"
        >
          ${plan.title}
        </gz-title>

        <div class="d-flex flex-row pt-2 pb-2">
          <gz-input
            class=${classMap({
              'color-red': plan.inputChanges?.seatChange || false,
            })}
            inputWidth="${InputWidthValues.SMALL}"
            type="text"
            label="${translateFn('Seats')}"
            value="${plan.seats}"
            readonly
            automationId="${automationIdPrefix}-seats-input"
          ></gz-input>
          ${plan.serverSeats
            ? html`<gz-input
                class="pl-2 ${classMap({
                  'color-red': plan.inputChanges?.serverChange || false,
                })}"
                inputWidth="${InputWidthValues.SMALL}"
                label="${translateFn('Servers')}"
                type="text"
                value="${plan.serverSeats}"
                readonly
                automationId="${automationIdPrefix}-servers-input"
              ></gz-input>`
            : nothing}
          ${plan.years
            ? html`<gz-input
                class="pl-2 ${classMap({
                  'color-red': isNewPlan,
                })}"
                inputWidth="${InputWidthValues.SMALL}"
                label="${translateFn('Years')}"
                type="text"
                value="${years}"
                readonly
                automationId="${automationIdPrefix}-years-input"
              ></gz-input>`
            : nothing}
        </div>
        <gz-input
          type="text"
          class="${classMap({
            'color-red': plan.inputChanges?.dateChange || false,
          })}"
          label="${translateFn('Expiry date')}"
          value="${moment(plan.availableUntil).format('D MMM YYYY')}"
          readonly
          automationId="${automationIdPrefix}-expiry-date-input"
        ></gz-input>
      </div>
    `;
  }

  private renderPrice(): TemplateResult | undefined {
    const priceConfig = this.config.priceConfig;
    if (priceConfig === undefined) {
      return;
    }

    return html`
      <div class="total-price pt-2">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_12}"
          fontWeight="${FontWeights.SEMIBOLD}"
          automationId="ipb-purchase-card-price-title"
        >
          ${translateFn('Your total price') + ':'}
        </gz-title>
        <div class="d-flex flex-column" style="height: 75px">
          ${priceConfig.loading
            ? html`
                <gz-progress-spinner
                  class="mt-1"
                  automationId="ipb-purchase-card-price-loading-spinner"
                  .spinnerwidth=${SpinnerWidth.SMALL}
                >
                </gz-progress-spinner>
              `
            : html`
                <div class="d-flex flex-row align-items-center">
                  <gz-text
                    class="w-auto"
                    fontSize="${FontSizes.HEADING_1}"
                    fontWeight="${FontWeights.SEMIBOLD}"
                    automationId="ipb-purchase-card-price-value-text"
                  >
                    ${priceConfig.totalPrice}
                  </gz-text>
                  <gz-text fontSize="${FontSizes.HEADING_1}" automationId="ipb-purchase-card-price-currency-text">
                    ${priceConfig.currency}
                  </gz-text>
                </div>
                <gz-text
                  fontSize="${FontSizes.PARAGRAPH_12}"
                  class="w-auto"
                  automationId="ipb-purchase-card-price-taxes-not-included-text"
                >
                  ${translateFn('Taxes not included')}
                </gz-text>
              `}
        </div>
      </div>
    `;
  }

  private renderMaxSeatsAlreadyExceededText(): TemplateResult | undefined {
    const config = this.config;
    const pageUrl = MAX_SEATS_EXCEEDED_PRODUCT_PAGES[config.productId];

    return html`
      <gz-text
        class="mt-2"
        automationId="ipb-purchase-card-max-seats-already-exceeded-text"
        .fontSize=${FontSizes.PARAGRAPH_12}
      >
        ${translateFn(
          'You can increase the number of your license seats up to {MAX_SEATS}. If you would like to extend your license further, purchase an additional license key for the same product and contact us at b2bcommercial@bitdefender.com.',
          {
            MAX_SEATS: config.maxSeats,
          }
        )}
      </gz-text>
      ${pageUrl
        ? html`
            <gz-link class="mt-1" automationId="ipb-purchase-card-max-seats-already-exceeded-link" .href=${pageUrl}>
              <gz-text .fontSize=${FontSizes.PARAGRAPH_12}>${translateFn('Purchase an additional key')}</gz-text>
            </gz-link>
          `
        : ''}
    `;
  }

  private renderBuyNewLicense(): TemplateResult | undefined {
    return html`
      <gz-text class="mt-2" automationId="ipb-purchase-card-buy-new-license-text" .fontSize=${FontSizes.PARAGRAPH_12}>
        ${translateFn(
          'License expired. Click the button below to purchase a new license. Make sure you enter the same company name.'
        )}
      </gz-text>
      ${this.renderBuyNewLicenseButton()}
    `;
  }

  private renderBuyButton(): TemplateResult | undefined {
    const config = this.config;
    const buyButtonDisabled =
      config.renewalNotAvailableYetText || config.priceConfig?.loading || config.buyButton.loading;

    const renewalNotAvailableYetTemplate = config.renewalNotAvailableYetText
      ? html`
          <gz-text
            class="mt-2"
            .fontSize=${FontSizes.PARAGRAPH_12}
            automationId="ipb-purchase-card-renewal-not-available-yet-text"
          >
            ${this.config.renewalNotAvailableYetText}
          </gz-text>
        `
      : undefined;

    const discountTextTemplate = html`
      <gz-text class="mt-2" .fontSize=${FontSizes.PARAGRAPH_12} automationId="ipb-purchase-card-discount-text">
        ${this.config.discountText}
      </gz-text>
    `;

    return html`
      <gz-button
        class="mt-2"
        automationId="ipb-purchase-card-buy-button"
        @click=${config.buyButton.buyEventHandlerFn}
        ?disabled=${buyButtonDisabled}
        ?showSpinner=${config.buyButton.loading}
      >
        ${config.buyButton.title}
      </gz-button>

      ${renewalNotAvailableYetTemplate ?? discountTextTemplate}
    `;
  }

  private renderBuyNewLicenseButton(): TemplateResult | undefined {
    const config = this.config;

    return html`
      <gz-button
        class="mt-3"
        automationId="ipb-purchase-card-buy-new-license-button"
        @click=${config.buyButton.buyEventHandlerFn}
      >
        ${config.buyButton.title}
      </gz-button>
    `;
  }

  private renderAutoRenewal(): TemplateResult | undefined {
    const autorenewal = this.config.autorenewal;
    if (autorenewal === undefined) {
      return;
    }

    return html`
      <div class="auto-renewal pt-2">
        <gz-labeled-content
          automationId="ipb-purchase-card-autorenewal-labeled-content"
          .label=${translateFn('Auto renewal')}
          .hasColon=${true}
        >
          <gz-text
            fontSize="${FontSizes.PARAGRAPH_12}"
            class="text-uppercase"
            automationId="ipb-purchase-card-autorenewal-text"
          >
            ${autorenewal ? translateFn('on') : translateFn('off')}
          </gz-text>
        </gz-labeled-content>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-purchase-card-layout': GzPurchaseCardLayoutWebComponent;
  }
}
